/**
 * pm-recart-mobile-position.js
 * Keeps Recart's dynamically inserted exit-intent dialog inside the viewport.
 *
 * Do NOT force pointer-events:auto on Recart's full-viewport overlay — Recart's
 * native overlay uses pointer-events:none so outside taps pass through / dismiss.
 * Forcing auto traps the page behind a dead backdrop when the close path stalls.
 */
(function () {
  "use strict";

  if (window.__pmRecartMobilePositionInit) return;
  window.__pmRecartMobilePositionInit = true;

  if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(window.location.pathname)) {
    var cartStyle = document.createElement("style");
    cartStyle.id = "pm-cart-recart-block";
    cartStyle.textContent = [
      "#recart-popup-root, #recart-widget-container {",
      "  display: none !important;",
      "  visibility: hidden !important;",
      "  opacity: 0 !important;",
      "  pointer-events: none !important;",
      "}"
    ].join("\n");
    document.head.appendChild(cartStyle);
    return;
  }

  var STYLE_ID = "pm-recart-mobile-position";
  var BOOT_MS = 250;
  var BOOT_MAX = 80; // ~20s
  var bootTries = 0;
  var bootTimer = null;
  var hostObserver = null;
  var documentObserver = null;
  var chatStateObserver = null;
  var observedHost = null;
  var shadowObserver = null;
  var observedShadow = null;
  var pinQueued = false;
  var pinBound = false;
  var scrollLocked = false;
  var savedScroll = null;
  var lastFreeScroll = null;
  var lockStyles = [];
  var LOCK_CLASS = "pm-recart-modal-lock";

  var CSS = [
    '[data-testid="sms-input-wrapper"] [data-testid="select-wrapper"],',
    '[data-testid="sms-input-wrapper"] [data-testid="select-wrapper"] [role="combobox"] {',
    '  outline: none !important;',
    '  box-shadow: none !important;',
    '}',
    ':host([data-pm-recart-keyboard]) [data-testid="sms-input-wrapper"] [data-testid="select-wrapper"]:focus-within::after {',
    '  content: "";',
    '  position: absolute;',
    '  inset: 1px;',
    '  border: 2px solid #111;',
    '  border-radius: inherit;',
    '  pointer-events: none;',
    '}',
    // A host attribute works in iPhone WebKit, unlike :host-context(). It also
    // covers minimized launchers that Recart inserts after the chat opens.
    ':host([data-pm-chat-suppresses-bubble]) [class*="custom-minimized"] {',
    '  display: none !important;',
    '}',
    '[class*="recart-popup-"][class*="custom-overlay"] {',
    "  position: fixed !important;",
    "  top: 0 !important;",
    "  left: 0 !important;",
    "  right: auto !important;",
    "  bottom: auto !important;",
    "  width: 100% !important;",
    "  height: 100% !important;",
    "  height: 100dvh !important;",
    "  box-sizing: border-box !important;",
    "  overscroll-behavior: none !important;",
    "  touch-action: none !important;",
    "  background: rgba(17, 17, 17, 0.46) !important;",
    "  -webkit-backdrop-filter: blur(8px) saturate(120%) !important;",
    "  backdrop-filter: blur(8px) saturate(120%) !important;",
    "}",
    '[role="dialog"][aria-modal="true"],',
    '[role="dialog"][class*="custom-wrapper"],',
    '[aria-modal="true"][class*="custom-wrapper"] {',
    "  position: fixed !important;",
    "  top: calc(env(safe-area-inset-top, 0px) + (100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) / 2) !important;",
    "  left: 50% !important;",
    "  right: auto !important;",
    "  bottom: auto !important;",
    "  max-height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px) !important;",
    "  width: min(700px, calc(100vw - 32px)) !important;",
    "  max-width: calc(100vw - 32px) !important;",
    "  min-height: 0 !important;",
    "  height: auto !important;",
    "  overflow-y: auto !important;",
    "  overflow-x: hidden !important;",
    "  overscroll-behavior: contain !important;",
    "  -webkit-overflow-scrolling: touch !important;",
    "  scroll-padding: 12px !important;",
    "  box-sizing: border-box !important;",
    "  transform: translate(-50%, -50%) !important;",
    "  margin: 0 !important;",
    "  background: #ffffff !important;",
    "  background-color: #ffffff !important;",
    "  pointer-events: auto !important;",
    "}",
    '[class*="recart-popup-"][class*="custom-wrapper"] button[class*="close"],',
    '[class*="recart-popup-"][aria-modal="true"] > button,',
    '[role="dialog"][class*="recart-popup-"] > button {',
    "  z-index: 5 !important;",
    "  pointer-events: auto !important;",
    "}",
    '@media (max-width: 1024px) {',
    '  [class*="custom-sms-content"],',
    '  [class*="custom-sms-content"] > div {',
    "    min-height: 0 !important;",
    "    height: auto !important;",
    "    margin: 0 !important;",
    "  }",
    '  [class*="custom-sms-content"] {',
    "    padding: 4px 0 2px !important;",
    "  }",
    '  [class*="custom-sms-content"] > div {',
    "    padding: 14px 16px 12px !important;",
    "    gap: 12px !important;",
    "    row-gap: 12px !important;",
    "  }",
    '  [class*="custom-sms-content"] form,',
    '  [class*="custom-sms-content"] form > div {',
    "    gap: 12px !important;",
    "    row-gap: 12px !important;",
    "    min-height: 0 !important;",
    "    height: auto !important;",
    "    margin: 0 !important;",
    "  }",
    '  [class*="custom-sms-content"] form > div > div {',
    "    gap: 10px !important;",
    "    row-gap: 10px !important;",
    "  }",
    '  [class*="custom-sms-content"] img {',
    "    margin: 0 auto !important;",
    "  }",
    '  [class*="custom-sms-content"] p {',
    "    margin: 0 !important;",
    "  }",
    "}",
    '[role="dialog"][aria-modal="true"] button,',
    '[role="dialog"][class*="custom-wrapper"] button,',
    '[aria-modal="true"][class*="custom-wrapper"] button,',
    '[role="dialog"][aria-modal="true"] input,',
    '[role="dialog"][class*="custom-wrapper"] input,',
    '[aria-modal="true"][class*="custom-wrapper"] input,',
    '[role="dialog"][aria-modal="true"] a,',
    '[role="dialog"][class*="custom-wrapper"] a,',
    '[aria-modal="true"][class*="custom-wrapper"] a {',
    "  pointer-events: auto !important;",
    "}",
    '[class*="recart-popup-"][class*="custom-minimized"],',
    '[class*="recart-popup-"][class*="custom-minimized"] button {',
    "  pointer-events: auto !important;",
    "}",
    '@media (max-width: 1024px) {',
    '  [class*="recart-popup-"][class*="custom-minimized"]:not([class*="custom-minimized-close"]) {',
    "    transform: scale(0.97) !important;",
    "    transform-origin: left bottom !important;",
    "  }",
    "}",
    '@media (max-width: 480px) {',
    '  [role="dialog"][aria-modal="true"],',
    '  [role="dialog"][class*="custom-wrapper"],',
    '  [aria-modal="true"][class*="custom-wrapper"] {',
    "    width: calc(100vw - 24px) !important;",
    "    max-width: calc(100vw - 24px) !important;",
    "    max-height: min(90dvh, 90vh) !important;",
    "  }",
    "}",
    "/* Only use the compact two-column layout in a genuinely wide, short layout viewport. */",
    "@media (max-width:1024px) and (max-height:520px) and (min-width:480px) {",
    "  [role=\"dialog\"][aria-modal=\"true\"] { overflow:hidden !important; display:flex !important; flex-direction:column !important; }",
    "  [class*=\"custom-wrapper\"] > button, button[aria-label=\"Close popup\"] {",
    "    position:absolute !important; top:16px !important; right:max(20px, env(safe-area-inset-right, 0px)) !important;",
    "    z-index:6 !important; width:44px !important; height:44px !important;",
    "  }",
    "  [class*=\"custom-sms-content\"] {",
    "    overflow-y:auto !important; overflow-x:hidden !important; min-height:0 !important; flex:1 !important; display:flex !important; align-items:safe center !important;",
    "    padding:0 !important; overscroll-behavior:contain !important; touch-action:pan-y !important;",
    "  }",
    "  [class*=\"custom-sms-content\"] > div { padding:28px max(28px, env(safe-area-inset-right, 0px)) 28px max(28px, env(safe-area-inset-left, 0px)) !important; width:100% !important; max-width:none !important; box-sizing:border-box !important; }",
    "  [class*=\"custom-sms-content\"] form > div {",
    "    display:grid !important; grid-template-columns:minmax(0,0.42fr) minmax(0,0.58fr) !important;",
    "    grid-template-rows:max-content max-content !important; column-gap:36px !important; row-gap:14px !important; align-content:center !important; align-items:center !important;",
    "    width:100% !important; max-width:820px !important; margin-inline:auto !important; min-width:0 !important;",
    "  }",
    "  [data-pm-land-logo] { grid-column:1 !important; grid-row:1 !important; align-self:center !important; margin:0 !important; height:112px !important; line-height:0 !important; }",
    "  [data-pm-land-logo] img { width:112px !important; height:112px !important; object-fit:contain !important; }",
    "  [data-pm-land-offer] { grid-column:1 !important; grid-row:2 !important; align-self:start !important; margin:0 !important; text-align:center !important; display:flex !important; flex-direction:column !important; gap:8px !important; padding:0 0 12px !important; }",
    "  [data-pm-land-offer] > div, [data-pm-land-offer] p { margin:0 !important; }",
    "  [data-pm-land-offer] > div:nth-child(1) { font-size:12px !important; line-height:1.2 !important; letter-spacing:0.14em !important; }",
    "  [data-pm-land-offer] > div:nth-child(2) { font-size:48px !important; line-height:1.1 !important; letter-spacing:-0.04em !important; }",
    "  [data-pm-land-offer] > div:nth-child(3), [data-pm-land-offer] > div:nth-child(3) p { font-size:14px !important; line-height:1.4 !important; }",
    "  [data-pm-land-form] {",
    "    grid-column:2 !important; grid-row:1 / span 2 !important;",
    "    display:flex !important; flex-direction:column !important; align-items:stretch !important;",
    "    width:100% !important; max-width:420px !important; margin-inline:auto !important; min-width:0 !important; gap:12px !important;",
    "  }",
    "  [data-pm-land-form] > button { width:100% !important; min-height:48px !important; font-size:16px !important; }",
    "  [data-pm-land-form] input { min-height:44px !important; font-size:16px !important; min-width:0 !important; }",
    "  [data-pm-land-form] > div:last-child, [data-pm-land-form] > div:last-child * { font-size:11px !important; line-height:1.35 !important; }",
    "}",
    "@media (max-width:1024px) and (min-width:480px) and (max-height:520px) {",
    " [data-pm-short-frame] { padding-top:48px !important; box-sizing:border-box !important; }",
    " [data-pm-short-frame] [class*=\"custom-sms-content\"] { padding-top:0 !important; box-sizing:border-box !important; }",
    " [data-pm-short-frame] [class*=\"custom-sms-content\"] > div { padding-top:8px !important; padding-bottom:16px !important; }",
    " [data-pm-short-frame] [class*=\"custom-sms-content\"] form > div { column-gap:24px !important; row-gap:8px !important; }",
    " [data-pm-short-frame] [data-pm-land-logo] { width:100% !important; height:64px !important; }",
    " [data-pm-short-frame] [data-pm-land-logo] img { width:64px !important; height:64px !important; }",
    " [data-pm-short-frame] [data-pm-land-offer] { gap:4px !important; padding-bottom:0 !important; }",
    " [data-pm-short-frame] [data-pm-land-offer] > div:nth-child(2) { font-size:30px !important; }",
    " [data-pm-short-frame] [data-pm-land-offer] > div:nth-child(3), [data-pm-short-frame] [data-pm-land-offer] > div:nth-child(3) p { font-size:12px !important; }",
    " [data-pm-short-frame] [data-pm-land-form] { gap:8px !important; align-self:start !important; }",
    " [data-pm-short-frame] [class*=\"custom-wrapper\"] > button, [data-pm-short-frame] > button { top:8px !important; width:32px !important; height:32px !important; }",
    " [data-pm-short-frame] [data-pm-land-form] > div:first-child { height:40px !important; min-height:40px !important; }",
    " [data-pm-short-frame] [data-testid=\"sms-input-wrapper\"] [data-testid=\"input-wrapper\"], [data-pm-short-frame] [data-testid=\"sms-input-wrapper\"] [data-testid=\"select-wrapper\"] { height:40px !important; min-height:40px !important; box-sizing:border-box !important; }",
    " [data-pm-short-frame] [data-testid=\"sms-input-wrapper\"] [data-testid=\"select-wrapper\"] > div, [data-pm-short-frame] [data-testid=\"sms-input-wrapper\"] [data-testid=\"select-wrapper\"] > button { height:100% !important; min-height:0 !important; padding-block:0 !important; box-sizing:border-box !important; }",
    " [data-pm-short-frame] [data-pm-land-form] input { height:40px !important; min-height:40px !important; padding-block:6px !important; }",
    " [data-pm-short-frame] [data-pm-land-form] > button { height:40px !important; min-height:40px !important; padding-block:6px !important; }",
    "}"
  ].join("\n");

  var LATE_ID = "pm-recart-landscape-late";
  var LATE_CSS = [
    "@media (max-width: 1024px) and (max-height: 520px) and (min-width: 480px) {",
    '  [class*="recart-popup-"][class*="custom-minimized"]:not([class*="custom-minimized-close"]) {',
    "    left: calc(10px + env(safe-area-inset-left, 0px)) !important;",
    "    right: auto !important;",
    "    bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;",
    "    top: auto !important;",
    "    width: auto !important;",
    "    max-width: 148px !important;",
    "    transform: none !important;",
    "    transform-origin: left bottom !important;",
    "  }",
    "  :host-context(html.pm-recart-modal-lock) [class*=\"custom-minimized\"] {",
    "    opacity: 0 !important;",
    "    visibility: hidden !important;",
    "    pointer-events: none !important;",
    "  }",
    "  :host-context(html.pm-chat-open) [class*=\"custom-minimized\"]:not([class*=\"custom-minimized-close\"]) {",
    "    opacity: 0 !important;",
    "    visibility: hidden !important;",
    "    pointer-events: none !important;",
    "  }",
    "}"
  ].join("\n");

  var WIDGET_CSS = [
    "@media (max-width: 1024px) and (max-height: 520px) and (min-width: 480px) {",
    "  #pm-chat-launcher {",
    "    width: 44px !important;",
    "    min-width: 44px !important;",
    "    height: 44px !important;",
    "    padding: 0 !important;",
    "    border-radius: 50% !important;",
    "    justify-content: center !important;",
    "    align-items: center !important;",
    "    left: auto !important;",
    "    right: calc(8px + env(safe-area-inset-right, 0px)) !important;",
    "    bottom: calc(8px + env(safe-area-inset-bottom, 0px)) !important;",
    "  }",
    "  #pm-chat-launcher svg { width:24px !important; height:24px !important; flex:0 0 24px !important; margin:0 !important; transform:none !important; }",
    "  #pm-chat-launcher .pm-chat-launcher-label {",
    "    display: none !important;",
    "  }",
    "  #pm-chat-nudge,",
    "  #pm-chat-nudge.show {",
    "    display: none !important;",
    "    visibility: hidden !important;",
    "    opacity: 0 !important;",
    "    pointer-events: none !important;",
    "  }",
    "  html.pm-recart-modal-lock #pm-chat-launcher,",
    "  html.pm-recart-modal-lock #pm-chat-nudge,",
    "  html.pm-recart-modal-lock #pm-chat-badge {",
    "    display: none !important;",
    "    visibility: hidden !important;",
    "    opacity: 0 !important;",
    "    pointer-events: none !important;",
    "  }",
    "}"
  ].join("\n");

  function getRecartHost() {
    return document.querySelector("#recart-popup-root");
  }

  function setCountryKeyboardMode(enabled) {
    var host = getRecartHost();
    if (host) host.toggleAttribute("data-pm-recart-keyboard", enabled);
  }

  document.addEventListener("keydown", function (event) {
    if (event.key === "Tab") setCountryKeyboardMode(true);
  }, true);
  document.addEventListener("pointerdown", function () {
    setCountryKeyboardMode(false);
  }, true);

  function syncChatBubbleSuppression() {
    var host = getRecartHost();
    if (!host) return;
    var chatOpen = document.documentElement.matches(".pm-chat-open,.pm-mini-chat-visible") ||
      (document.body && document.body.matches(".pm-chat-open,.pm-mini-chat-visible")) ||
      document.querySelector("#pm-chat-nudge.show, #pm-chat-window.open");
    // Hide only the launcher inside Recart, never its host or the page.
    var suppressed = mobilePopup() && !!chatOpen;
    host.toggleAttribute("data-pm-chat-suppresses-bubble", suppressed);
    // The chat's older cleanup only finds visible launchers. Release its
    // marked inline overrides after our shadow rule has hidden the bubble.
    if (!suppressed && host.shadowRoot) {
      host.shadowRoot.querySelectorAll("[data-pm-chat-hidden-recart], [data-pm-mini-chat-hidden]").forEach(function (node) {
        if (node.hasAttribute("data-pm-mini-chat-hidden")) {
          node.removeAttribute("data-pm-mini-chat-hidden");
          node.style.removeProperty("visibility");
        }
        node.removeAttribute("data-pm-chat-hidden-recart");
        node.style.removeProperty("opacity");
        node.style.removeProperty("pointer-events");
      });
    }
  }

  function observeChatState() {
    if (chatStateObserver || !document.body) return;
    chatStateObserver = new MutationObserver(syncChatBubbleSuppression);
    var options = { attributes: true, attributeFilter: ["class"] };
    chatStateObserver.observe(document.documentElement, options);
    chatStateObserver.observe(document.body, options);
  }

  function ensureStyle(shadow) {
    var style = shadow.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      shadow.appendChild(style);
    }
    if (style.textContent !== CSS) {
      style.textContent = CSS;
    }
    return style;
  }

  function ensureLateStyle(shadow) {
    var style = shadow.getElementById(LATE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = LATE_ID;
    }
    if (style.textContent !== LATE_CSS) style.textContent = LATE_CSS;
    if (!style.parentNode) shadow.appendChild(style);
  }

  function ensureWidgetStyle() {
    var style = document.getElementById("pm-recart-landscape-widgets");
    if (!style) {
      style = document.createElement("style");
      style.id = "pm-recart-landscape-widgets";
      document.head.appendChild(style);
    }
    if (style.textContent !== WIDGET_CSS) style.textContent = WIDGET_CSS;
  }

  function ensureShadowObserver(shadow) {
    if (observedShadow === shadow && shadowObserver) return;

    if (shadowObserver) {
      shadowObserver.disconnect();
      shadowObserver = null;
    }

    observedShadow = shadow;
    shadowObserver = new MutationObserver(function (mutations) {
      if (!shadow.getElementById(STYLE_ID)) {
        ensureStyle(shadow);
      }
      if (mutations.some(function (mutation) { return mutation.addedNodes && mutation.addedNodes.length; })) {
        schedulePin();
      }
    });
    /* Do not observe style attributes — Recart + chat widget rewrite inline
       styles while the popup is open; watching them re-enters ensureStyle churn. */
    shadowObserver.observe(shadow, {
      childList: true,
      subtree: true
    });
    schedulePin();
  }

  function safeInsets() {
    var probe = document.getElementById("pm-recart-safe-probe");
    if (!probe) {
      probe = document.createElement("div");
      probe.id = "pm-recart-safe-probe";
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText = "position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);";
      (document.documentElement || document.body).appendChild(probe);
    }
    var styles = window.getComputedStyle(probe);
    return {
      top: parseFloat(styles.paddingTop) || 0,
      right: parseFloat(styles.paddingRight) || 0,
      bottom: parseFloat(styles.paddingBottom) || 0,
      left: parseFloat(styles.paddingLeft) || 0
    };
  }

  function shortLandscape() {
    if (!mobilePopup()) return false;
    // Match the layout breakpoint; a portrait keyboard must not select a landscape form.
    return window.matchMedia("(max-width: 1024px) and (max-height: 520px) and (min-width: 480px)").matches;
  }

  function focusedField(shadow) {
    var active = shadow && shadow.activeElement;
    return active && active.tagName === "INPUT" ? active : null;
  }

  function visibleFrame() {
    var vv = window.visualViewport;
    return { top: vv ? vv.offsetTop : 0, left: vv ? vv.offsetLeft : 0,
      height: vv ? vv.height : window.innerHeight, width: vv ? vv.width : window.innerWidth };
  }

  function stackBrand(dialog, landscape) {
    var column = dialog.querySelector('[class*="custom-sms-content"] form > div');
    if (!column || column.children.length < 2) return;
    var logo = column.querySelector("[data-pm-land-logo]") || column.children[0];
    var offer = column.querySelector("[data-pm-land-offer]");
    var fields = column.querySelector("[data-pm-land-form]");
    if (!offer) {
      offer = logo.nextElementSibling;
      fields = offer && offer.nextElementSibling;
    }
    if (!logo || !offer || !fields) return;
    logo.setAttribute("data-pm-land-logo", "1");
    offer.setAttribute("data-pm-land-offer", "1");
    fields.setAttribute("data-pm-land-form", "1");
    // Keep vendor-owned React children in their original parent and order.
  }

  function revealFocused(input) {
    if (!input || !input.closest) return;
    var scroller = input.closest('[class*="custom-sms-content"]');
    if (!scroller) return;
    var field = input.getBoundingClientRect();
    var frame = scroller.getBoundingClientRect();
    if (field.top < frame.top + 8) scroller.scrollTop += field.top - frame.top - 8;
    else if (field.bottom > frame.bottom - 8) scroller.scrollTop += field.bottom - frame.bottom + 8;
  }

  function placeMinimized(shadow) {
    if (!shadow || !mobilePopup()) return;
    var nodes = shadow.querySelectorAll('[class*="custom-minimized"]');
    for (var i = 0; i < nodes.length; i++) {
      if (/custom-minimized-close/.test(nodes[i].className || "")) continue;
      setImportant(nodes[i], {
        left: "calc(10px + env(safe-area-inset-left, 0px))",
        right: "auto",
        bottom: (Math.max(0, window.innerHeight - (visibleFrame().top + visibleFrame().height)) + (shortLandscape() ? 8 : 26) + safeInsets().bottom) + "px",
        transition: "none",
        top: "auto",
        transform: "none"
      });
    }
  }

  function setImportant(el, props) {
    Object.keys(props).forEach(function (name) {
      var value = props[name];
      if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value, "important");
    });
  }

  function visiblePopup(el) {
    if (!el) return false;
    if (el.getAttribute("data-pm-modal-hidden") === "1") return false;
    if (el.getAttribute("aria-hidden") === "true" || el.hasAttribute("hidden")) return false;
    var styles = window.getComputedStyle(el);
    return styles.display !== "none" && styles.visibility !== "hidden" && parseFloat(styles.opacity || "1") > 0;
  }

  function mobilePopup() {
    return window.matchMedia("(max-width: 1024px)").matches;
  }

  function ensureLockStyle() {
    if (document.getElementById("pm-recart-modal-lock-style")) return;
    var style = document.createElement("style");
    style.id = "pm-recart-modal-lock-style";
    style.textContent = [
      "html." + LOCK_CLASS + " body.pm-homepage-hero-floating-hidden #recart-popup-root { opacity:1 !important; pointer-events:auto !important; }",
      "html." + LOCK_CLASS + " #pm-chat-root,",
      "html." + LOCK_CLASS + " #pm-chat-launcher,",
      "html." + LOCK_CLASS + " #pm-chat-nudge,",
      "html." + LOCK_CLASS + " #pm-chat-badge {",
      "  opacity: 0 !important;",
      "  visibility: hidden !important;",
      "  pointer-events: none !important;",
      "}"
    ].join("\n");
    document.head.appendChild(style);
  }

  function readScroll() {
    return {
      win: window.scrollY || window.pageYOffset || 0,
      body: document.body ? document.body.scrollTop : 0,
      root: document.scrollingElement ? document.scrollingElement.scrollTop : 0
    };
  }

  function rememberFreeScroll() {
    if (!scrollLocked) lastFreeScroll = readScroll();
  }

  function lockScroll() {
    if (!mobilePopup() || scrollLocked || !document.body) return;
    savedScroll = readScroll();
    scrollLocked = true;
    var y = savedScroll.win || savedScroll.root || savedScroll.body || 0;
    lockStyles = ["position", "top", "left", "right", "width", "overflow"].map(function(key) {
      return [key, document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)];
    });
    ensureLockStyle();
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
    setImportant(document.body, { position:"fixed", top: -y + "px", left:"0", right:"0", width:"100%", overflow:"hidden" });
  }

  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    lockStyles.forEach(function(item) {
      if (item[1]) document.body.style.setProperty(item[0], item[1], item[2]);
      else document.body.style.removeProperty(item[0]);
    });
    lockStyles = [];
    var y = savedScroll.win || savedScroll.root || savedScroll.body || 0;
    window.scrollTo(0,y);
    savedScroll = null;
    rememberFreeScroll();
  }

  function hideOverlay(overlay) {
    if (!overlay || overlay.getAttribute("data-pm-modal-hidden") === "1") return;
    overlay.setAttribute("data-pm-modal-hidden", "1");
    setImportant(overlay, {
      visibility: "hidden",
      opacity: "0",
      "pointer-events": "none"
    });
  }

  function showOverlay(overlay) {
    if (!overlay || overlay.getAttribute("data-pm-modal-hidden") !== "1") return;
    overlay.removeAttribute("data-pm-modal-hidden");
    ["visibility", "opacity", "pointer-events"].forEach(function (name) {
      overlay.style.removeProperty(name);
    });
  }

  function gapAfterDisclaimer(dialog) {
    var links = dialog.querySelectorAll("a");
    var link = null;
    for (var i = 0; i < links.length; i++) {
      if (/terms of service/i.test(links[i].textContent || "")) {
        link = links[i];
        break;
      }
    }
    if (!link) return;
    var block = link.parentElement;
    while (block && block !== dialog && block.parentElement && block.parentElement !== dialog) {
      var parent = block.parentElement;
      if (parent.getBoundingClientRect().height > 200) break;
      block = parent;
    }
    if (!block || block === dialog) block = link.parentElement;
    if (!block) return;
    block.style.setProperty("padding-bottom", "12px", "important");
    block.style.setProperty("margin-bottom", "0px", "important");
  }

  function touchInsideDialog(event) {
    var path = event.composedPath ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || !node.getAttribute) continue;
      if (node.getAttribute("role") === "dialog" || node.getAttribute("aria-modal") === "true") return true;
    }
    return false;
  }

  function pinPopup() {
    pinQueued = false;
    syncChatBubbleSuppression();
    // Print A Bill hides the Recart popup. Never lock the entire page for a
    // popup the customer cannot see, including after Recart's delayed boot.
    if (document.body && (document.body.classList.contains("page-print-a-bill") || document.body.classList.contains("page-print-a-bill-tester"))) {
      unlockScroll();
      return;
    }
    var host = getRecartHost();
    var shadow = host && host.shadowRoot;
    if (!shadow) return;
    var dialog = shadow.querySelector('[role="dialog"][aria-modal="true"], [role="dialog"][class*="custom-wrapper"], [aria-modal="true"][class*="custom-wrapper"]');
    var overlay = shadow.querySelector('[class*="recart-popup-"][class*="custom-overlay"]');
    ensureLateStyle(shadow);
    placeMinimized(shadow);
    var blocked = document.documentElement.matches(".pm-chat-open,.pm-mobile-search-open,.pm-search-ui-active") || document.body.classList.contains("mobile-nav-open");
    if (!visiblePopup(dialog) || blocked) {
      if (mobilePopup()) hideOverlay(overlay);
      unlockScroll();
      ["position", "top", "left", "width", "height", "opacity", "transform", "writing-mode", "overflow", "background-color"].forEach(function (name) {
        host.style.removeProperty(name);
      });
      return;
    }

    if (mobilePopup()) {
      showOverlay(overlay);
      lockScroll();
      gapAfterDisclaimer(dialog);
      if (!dialog.dataset.pmGapBound) {
        dialog.dataset.pmGapBound = "1";
        [80, 400].forEach(function (delay) {
          window.setTimeout(function () {
            if (visiblePopup(dialog)) gapAfterDisclaimer(dialog);
          }, delay);
        });
      }
    }

    var frame = visibleFrame(shadow);
    var top = frame.top;
    var left = frame.left;
    var height = frame.height;
    var width = frame.width;
    var safe = safeInsets();
    var landscape = shortLandscape();
    var shortFrame = landscape && (height < 280 || (focusedField(shadow) && height < window.innerHeight - 60));
    if (shortFrame) dialog.setAttribute("data-pm-short-frame", "1");
    else dialog.removeAttribute("data-pm-short-frame");
    var gutter = landscape ? 0 : 12;
    setImportant(host, {
      position: "fixed",
      top: Math.round(top) + "px",
      left: Math.round(left) + "px",
      width: Math.round(width) + "px",
      height: Math.round(landscape ? Math.max(height, window.innerHeight) : height) + "px",
      "background-color": landscape ? "#ffffff" : "transparent",
      opacity: "1",
      transform: "none",
      "writing-mode": "horizontal-tb"
    });
    var maxHeight = Math.max(1, Math.floor(landscape ? height : height - gutter * 2 - safe.top - safe.bottom));
    if (overlay) setImportant(overlay, {position:"fixed", top:top+"px", left:left+"px",
      width:width+"px", height:Math.max(height, window.innerHeight)+"px", right:"auto", bottom:"auto", transform:"none",
      background:landscape ? "#ffffff" : "rgba(17, 17, 17, 0.46)",
      "backdrop-filter":landscape ? "none" : "blur(8px) saturate(120%)",
      "-webkit-backdrop-filter":landscape ? "none" : "blur(8px) saturate(120%)"});
    var cardWidth = landscape ? width : Math.max(1, Math.min(700, width-safe.left-safe.right-gutter*2));
    stackBrand(dialog, landscape);
    setImportant(dialog, {position:"fixed", top:(top+safe.top+(height-safe.top-safe.bottom)/2)+"px",
      left:(left+safe.left+(width-safe.left-safe.right)/2)+"px", right:"auto", bottom:"auto",
      transform:"translate(-50%, -50%)", margin:"0", width:cardWidth+"px", "max-width":cardWidth+"px",
      "max-height":maxHeight+"px", "min-height":"0", height:landscape ? maxHeight+"px" : "auto", overflow:"hidden"});
    if (landscape) {
      setImportant(dialog, {top:top+"px",left:left+"px",transform:"none","border-radius":"0"});
    } else dialog.style.removeProperty("border-radius");
    var content = dialog.querySelector('[class*="custom-sms-content"]');
    if (content) setImportant(content, {"max-height":Math.max(1, maxHeight - (shortFrame ? 48 : 0))+"px", "overflow-y":"auto", "min-height":"0"});
    var field = focusedField(shadow);
    if (field) revealFocused(field);
  }

  function schedulePin() {
    if (pinQueued) return;
    pinQueued = true;
    requestAnimationFrame(pinPopup);
  }

  function bindPinListeners() {
    if (pinBound) return;
    pinBound = true;
    window.addEventListener("resize", schedulePin, { passive: true });
    document.addEventListener("scroll", function () {
      if (!scrollLocked) rememberFreeScroll();
      schedulePin();
    }, { passive: true, capture: true });
    window.setInterval(rememberFreeScroll, 250);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", schedulePin, { passive: true });
      window.visualViewport.addEventListener("scroll", schedulePin, { passive: true });
    }
    var fieldsBound = false;
    function bindFieldListeners() {
      if (fieldsBound) return;
      var host = getRecartHost();
      var shadow = host && host.shadowRoot;
      if (!shadow) return;
      fieldsBound = true;
      shadow.addEventListener("focusin", function (event) {
        if (!event.target || event.target.tagName !== "INPUT") return;
        schedulePin();
        [80, 240, 500, 800].forEach(function (delay) {
          window.setTimeout(function () {
            schedulePin();
            revealFocused(event.target);
          }, delay);
        });
      }, true);
      shadow.addEventListener("focusout", function () {
        [60, 220, 480].forEach(function (delay) {
          window.setTimeout(schedulePin, delay);
        });
      }, true);
    }

    window.addEventListener("orientationchange", function () {
      schedulePin();
      [50, 150, 400, 800].forEach(function (delay) {
        window.setTimeout(schedulePin, delay);
      });
    }, { passive: true });
    document.addEventListener("touchmove", function (event) {
      if (!scrollLocked || touchInsideDialog(event)) return;
      event.preventDefault();
    }, { passive: false, capture: true });
    window.addEventListener("wheel", function (event) {
      if (!scrollLocked || touchInsideDialog(event)) return;
      event.preventDefault();
    }, { passive: false, capture: true });
    bindFieldListeners();
  }

  function ensureHostObserver(host) {
    if (hostObserver && observedHost === host) return;
    if (hostObserver) hostObserver.disconnect();
    observedHost = host;
    hostObserver = new MutationObserver(function () {
      inject();
    });
    hostObserver.observe(host, {
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden", "aria-hidden"]
    });
  }

  function ensureDocumentObserver() {
    if (documentObserver || !document.body) return;
    documentObserver = new MutationObserver(function () {
      var host = getRecartHost();
      if (host && host !== observedHost) inject();
    });
    documentObserver.observe(document.body, { childList: true, subtree: true });
  }

  function inject() {
    ensureDocumentObserver();
    observeChatState();
    var host = getRecartHost();
    var shadow = host && host.shadowRoot;
    syncChatBubbleSuppression();
    if (!host || !shadow) return false;

    ensureWidgetStyle();
    ensureStyle(shadow);
    ensureLateStyle(shadow);
    ensureHostObserver(host);
    ensureShadowObserver(shadow);
    bindPinListeners();
    schedulePin();
    window.setTimeout(schedulePin, 400);
    window.setTimeout(schedulePin, 1200);
    return true;
  }

  function boot() {
    bootTries += 1;
    if (inject()) {
      if (bootTimer) {
        window.clearInterval(bootTimer);
        bootTimer = null;
      }
      return;
    }

    if (bootTries >= BOOT_MAX && bootTimer) {
      window.clearInterval(bootTimer);
      bootTimer = null;
    }
  }

  boot();
  if (!getRecartHost() || !(getRecartHost().shadowRoot)) {
    bootTimer = window.setInterval(boot, BOOT_MS);
  }
})();


